import React, { useState, useMemo } from 'react';
import { Dropdown, Space, Tag, Typography, Avatar, Input, Badge } from 'antd';
import { SwapOutlined, ShopOutlined, CheckCircleFilled, SearchOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useAuthStore } from '../store/authStore';

const { Text } = Typography;

const planColors: Record<string, string> = {
  free: 'default',
  starter: 'green',
  business: 'blue',
  enterprise: 'gold',
};

const planLabels: Record<string, string> = {
  free: 'СТАРТ',
  starter: 'РОСТ',
  business: 'БИЗНЕС',
  enterprise: 'ПРЕМИУМ',
};

const roleLabels: Record<string, string> = {
  owner: 'Владелец',
  admin: 'Администратор',
  manager: 'Менеджер',
  operator: 'Оператор',
};

const TenantSwitcher: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const { tenant, availableTenants, switchTenant, isSwitching } = useAuthStore();
  const [searchQuery, setSearchQuery] = useState('');

  // Фильтрация тенантов по поисковому запросу
  const filteredTenants = useMemo(() => {
    if (!availableTenants) return [];
    if (!searchQuery.trim()) return availableTenants;
    const q = searchQuery.trim().toLowerCase();
    return availableTenants.filter(t => t.name.toLowerCase().includes(q));
  }, [availableTenants, searchQuery]);

  // Не показываем если тенант один
  if (!availableTenants || availableTenants.length < 1) {
    return null;
  }

  const menuItems: MenuProps['items'] = [
    {
      key: 'search',
      label: (
        <div style={{ padding: '4px 0' }} onClick={e => e.stopPropagation()}>
          <Input
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            placeholder="Поиск компании..."
            size="small"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            allowClear
            style={{ width: '100%' }}
          />
        </div>
      ),
      disabled: true,
    },
    { type: 'divider' },
    {
      key: 'header',
      type: 'group',
      label: (
        <Text strong style={{ fontSize: 12, color: '#8c8c8c' }}>
          ПРОЕКТЫ ({filteredTenants.length})
        </Text>
      ),
    },
    ...(filteredTenants.length === 0
      ? [{
          key: 'empty',
          label: (
            <div style={{ textAlign: 'center', padding: '12px 0', color: '#bfbfbf' }}>
              Ничего не найдено
            </div>
          ),
          disabled: true,
        }]
      : filteredTenants.map((t) => ({
          key: String(t.id),
          label: (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '4px 0',
              minWidth: 240,
            }}>
              <Space size={8}>
                <Avatar
                  size={32}
                  src={t.logo_url}
                  style={{
                    backgroundColor: t.is_current ? '#FF6B35' : '#f0f0f0',
                    color: t.is_current ? '#fff' : '#666',
                    fontSize: 14,
                  }}
                >
                  {t.name?.charAt(0)?.toUpperCase()}
                </Avatar>
                <div>
                  <div style={{ fontWeight: t.is_current ? 600 : 400, lineHeight: 1.3 }}>
                    {t.name}
                    {t.is_current && (
                      <CheckCircleFilled style={{ color: '#52c41a', marginLeft: 6, fontSize: 12 }} />
                    )}
                  </div>
                  <Space size={4}>
                    <Tag
                      color={planColors[t.plan] || 'default'}
                      style={{ fontSize: 10, lineHeight: '16px', margin: 0, padding: '0 4px' }}
                    >
                      {planLabels[t.plan] || t.plan}
                    </Tag>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {roleLabels[t.role] || t.role}
                    </Text>
                  </Space>
                </div>
              </Space>
            </div>
          ),
          disabled: t.is_current,
        }))),
  ];

  const handleMenuClick: MenuProps['onClick'] = async ({ key }) => {
    const tenantId = parseInt(key);
    if (!isNaN(tenantId) && tenantId !== tenant?.id) {
      await switchTenant(tenantId);
    }
  };

  return (
    <Dropdown
      menu={{ items: menuItems, onClick: handleMenuClick }}
      placement="bottomLeft"
      trigger={['click']}
      disabled={isSwitching}
      onOpenChange={(open) => { if (!open) setSearchQuery(''); }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        cursor: 'pointer',
        padding: '4px 12px',
        borderRadius: 6,
        border: '1px solid #d9d9d9',
        background: '#fafafa',
        transition: 'all 0.2s',
        maxWidth: compact ? 170 : undefined,
      }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#FF6B35';
          e.currentTarget.style.background = '#fff5f0';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#d9d9d9';
          e.currentTarget.style.background = '#fafafa';
        }}
      >
        <ShopOutlined style={{ color: '#FF6B35', fontSize: 16 }} />
        <div style={{ lineHeight: 1.2, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: '#262626',
            overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
            {tenant?.name || 'Проект'}
          </div>
          {!compact && <div style={{ fontSize: 10, color: '#8c8c8c' }}>
            {planLabels[tenant?.plan || ''] || tenant?.plan}
          </div>}
        </div>
        {!compact && <SwapOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />}
        {!compact && <Badge
          count={availableTenants.length}
          size="small"
          style={{ backgroundColor: '#FF6B35' }}
        />}
      </div>
    </Dropdown>
  );
};

export default TenantSwitcher;
